import { memo, useEffect, useRef } from "react";

import { formatDateTime, mergeObject, replaceKey } from "../../utils";
import { useCompanySettings, useTemplateSetting } from "../../hooks/useCompanySettings";
import i18n from "../../locale/i18n";
import { CHART_PATTERN, SERIES_LINE_CHART_TYPE, SETTING_FORMAT, TOOL_TEMPLATE } from "../../constant/common";
import { getCurrencyLabel } from "../../store/utils";
import { useSelector } from "react-redux";
import { colorBlindModeSelector } from "../../store/slice/settings";
import {
  HighChartWrapper,
  defaultOptions,
  tooltipOptions,
  plotOptions,
  xAxisLabelFormatCreator,
  normalizeData,
  getSvgOfDashStyle,
} from "@euroland/react-high-chart";
import { formatDate, getDate } from "@euroland/libs";
import Highcharts from "./setup";
import useCurrencyName from "../../hooks/useCurrencyName";
import TickerLengendMultipleLineChart from "./TickerLengendMultipleLineChart";

/**
 * @typedef { import('../../..').IHistoricalPrices } IHistoricalPrices
 * @typedef { import('../../..').IInstrumentSetting } IInstrumentSetting
 * @typedef { import('../../..').IPeerSetting } IPeerSetting
 * @typedef {{series: IHistoricalPrices[]}} HighChartMultipleLineProps
 * @typedef { import('../../store/slice/trade').IInstrumentDataAndSettingSelectorReturn } IInstrumentDataAndSettingSelectorReturn
 * @typedef { import('../../..').CompanySetting } CompanySetting
 */

/**
 * @param {{
 * series: IHistoricalPrices[],
 * instrumentDataAndSetting: IInstrumentDataAndSettingSelectorReturn
 * settings: CompanySetting
 * }} param0
 */
const createOptionsQuadrata =
  ({ series, instrumentDataAndSetting, settings, currencyCode, colorBlind, getCurrencyNameByCode, formatNumber }) =>
  chartUtils => {
    const { loadStyle } = chartUtils;
    const options = defaultOptions(chartUtils);
    const xAxis = [
      mergeObject(options.xAxis, {
        type: "datetime",
        labels: {
          formatter: xAxisLabelFormatCreator({ formatDate, getDate }),
        },
      }),
    ];
    const yAxis = [
      mergeObject(options.yAxis, {
        title: {
          text: currencyCode,
        },
        labels: {
          formatter: function () {
            return formatNumber(this.value, {decimalDigits: 0});
          },
        },
      }),
    ];

    const plotOptionsSpline = plotOptions.spline(chartUtils);
    if (colorBlind) {
      delete plotOptionsSpline.marker.symbol;
      delete plotOptionsSpline.marker.states.hover.symbol;
      // options.legend.enabled = true;
      // options.legend.symbolWidth = 40;
    }

    const result = mergeObject(options, {
      accessibility: {
        enabled: true,
        series: {
          pointDescriptionEnabledThreshold: false,
        }
      },
      chart: {
        animation: false,
      },
      xAxis,
      yAxis,
      tooltip: mergeObject(tooltipOptions(chartUtils), {
        formatter: function () {
          return `
          <div class="custom-tooltip">
            <div role="region" class="custom-tooltip__time"  aria-label="${replaceKey(i18n.translate("valueOnLabel"), [
              formatDate(this.x, SETTING_FORMAT.FULL_DATE),
            ])}">
            ${replaceKey(i18n.translate("valueOnLabel"), [formatDateTime(this.x, settings)])}</div>
            ${this.points
              .reverse()
              .map(item => {
                return `
                      <div class="custom-tooltip__item">
                      ${
                        colorBlind
                          ? getSvgOfDashStyle({
                              color: item.color,
                              lineWidth: item.series.options.lineWidth,
                              dashStyle: item.series.options.dashStyle,
                            })
                          : `<span class="custom-tooltip__color" style="background-color: ${item.color};"></span>`
                      }
                      <div class="custom-tooltip__item_right">
                        <span class="custom-tooltip__label">${i18n.translate(item.series.options.id)}</span>
                        <p>
                        <span role="region" class="custom-tooltip__currency" aria-label="${getCurrencyNameByCode(
                          currencyCode
                        )}">${currencyCode}</span>
                        <span class="custom-tooltip__value">${formatNumber(item.y)}</span>
                        </p>
                        </div>
                      </div>
                  `;
              })
              .join("")}
          </div>
        `;
        },
      }),

      plotOptions: {
        series: {
          connectNulls: true,
        },
        column: { tooltip: { enabled: false } },
        spline: plotOptionsSpline,
      },
      series: series.map(item => {
        const instrument = instrumentDataAndSetting[item.instrumentId];
        const normalize = normalizeData(item.datas.map(item => [new Date(item.date).getTime(), item.value]));
        const hasDividend = series.some(s => s.id === SERIES_LINE_CHART_TYPE.TOTAL_RETURN);
        let color = "";
        let dashStyle =
          colorBlind && item.id === SERIES_LINE_CHART_TYPE.TOTAL_RETURN
            ? CHART_PATTERN.SHORT_DASH
            : CHART_PATTERN.SOLID;
        switch (item.id) {
          case SERIES_LINE_CHART_TYPE.VALUE:
            color = instrument.setting.color || loadStyle("--chart-line-color-value", "#11A55E").get()
            break;
          default:
            color = loadStyle("--chart-line-color-total", "#404D61").get()

            break;
        }

        return {
          color,
          hasDividend,
          type: "spline",
          //name: instrument.data.ticker + " " + `(${instrument.data.marketAbbreviation})`,
          name:
            item.id === SERIES_LINE_CHART_TYPE.TOTAL_RETURN
              ? i18n.translate("totalReturn")
              : i18n.translate("value"),
          id: item.id,
          instrumentId: item.instrumentId,
          data: normalize.data,
          currency: currencyCode,
          dashStyle: dashStyle,
        };
      }),
    });
    
    return result;
  };

const createOptionsVictor = options => chartUtils => {
  const victorOption = createOptionsQuadrata(options)(chartUtils);
  const { loadStyle } = chartUtils;
  const { currencyCode, settings, instrumentDataAndSetting, getCurrencyNameByCode, colorBlind , formatNumber} = options;

  victorOption.yAxis[0].labels.formatter = function () {
    return formatNumber(this.value, {decimalDigits: 0});
  };

  victorOption.series = victorOption.series.reverse().map(item => {
    let color = "";
    const instrument = instrumentDataAndSetting[item.instrumentId];
    switch (item.id) {
      case SERIES_LINE_CHART_TYPE.VALUE:
        color = instrument.setting.color || loadStyle("--chart-line-color-value", "#2F80ED").get();
        break;
      default:
        color = loadStyle("--chart-line-color-total", "#F2994A").get();
        break;
    }

    return {
      ...item,
      color,
      dataLabels: {
        enabled: !item.hasDividend,
        useHTML: true,
        formatter: function () {
          const pointY = this.point.y;
          const pointX = this.point.x;
          if (
            (pointX === item.data[0][0] && pointY === item.data[0][1]) ||
            (pointX === item.data[item.data.length - 1][0] && pointY === item.data[item.data.length - 1][1])
          ) {
            return `<div class="line-point">
        <div role="region" class="custom-tooltip__time" aria-label="${formatDate(
          this.x,
          SETTING_FORMAT.FULL_DATE
        )}">${formatDateTime(this.x, settings)}</div>
        <span class="line-point__label">${i18n.translate("value")}</span>
        <span class="line-point__value">${formatNumber(pointY)}</span>
        <span role="region" class="line-point__currency" aria-label="${getCurrencyNameByCode(
          currencyCode
        )}">(${getCurrencyLabel(currencyCode)})</span>
      </div>`;
          }
        },
      },
    };
  });
  victorOption.tooltip.formatter = function () {
    return `
      <div class="custom-tooltip">
        <div role="region" class="custom-tooltip__time" aria-label="${formatDate(
          this.x,
          SETTING_FORMAT.FULL_DATE
        )}">${formatDateTime(this.x, settings)}</div>
        ${this.points
          .map(item => {
            return `
          <div class="custom-tooltip__item">
                  <div class="custom-tooltip__item_right">
                    ${
                      colorBlind
                        ? getSvgOfDashStyle({
                            color: item.color,
                            lineWidth: item.series.options.lineWidth,
                            dashStyle: item.series.options.dashStyle,
                          })
                        : ""
                    }
                    <span class="custom-tooltip__label">${i18n.translate(item.series.options.id)}</span>
                    <span class="custom-tooltip__value" ${
                      item.series.options.hasDividend ? `style="color:${item.color}` : ""
                    }"}>${formatNumber(item.y)}</span>
                    <span role="region" class="custom-tooltip__currency" aria-label="${getCurrencyNameByCode(
                      currencyCode
                    )}">(${getCurrencyLabel(currencyCode)})</span>
                    </div>
                  </div>
              `;
          })
          .join("")}
      </div>
    `;
  };
  return victorOption;
};

const HighChartMultipleLineVictor = memo(
  (/** @type { HighChartMultipleLineProps } */ { series, instrumentDataAndSetting, currencyCode, formatNumber }) => {
    const colorBlind = useSelector(colorBlindModeSelector);
    const settings = useCompanySettings();
    const getCurrencyNameByCode = useCurrencyName();
    const chartRef = useRef();

    return (
      <>
        <HighChartWrapper
          ref={chartRef}
          className="highchart-multiple-line"
          createOptions={createOptionsVictor({
            series,
            instrumentDataAndSetting,
            settings,
            currencyCode,
            colorBlind,
            getCurrencyNameByCode,
            formatNumber
          })}
          highCharts={Highcharts}
          allowChartUpdate
          isRtl={window.appSettings.isRtl}
          immutable={true}
        />
        <TickerLengendMultipleLineChart
          series={series}
          currencyCode={currencyCode}
          instrumentDataAndSetting={instrumentDataAndSetting}
          chartRef={chartRef}
        />
      </>
    );
  },
  (prev, next) => {
    return prev.series === next.series;
  }
);

const HighChartMultipleLineQuadrata = memo(
  (/** @type { HighChartMultipleLineProps } */ { series, instrumentDataAndSetting, currencyCode, formatNumber }) => {
    const colorBlind = useSelector(colorBlindModeSelector);
    const settings = useCompanySettings();
    const getCurrencyNameByCode = useCurrencyName();
    const chartRef = useRef();

    return (
      <>
        <HighChartWrapper
          ref={chartRef}
          className="highchart-multiple-line"
          createOptions={createOptionsQuadrata({
            series,
            instrumentDataAndSetting,
            settings,
            currencyCode,
            colorBlind,
            getCurrencyNameByCode,
            formatNumber
          })}
          isRtl={window.appSettings.isRtl}
          highCharts={Highcharts}
          allowChartUpdate
          immutable={true}
        />
        <TickerLengendMultipleLineChart
          series={series}
          currencyCode={currencyCode}
          instrumentDataAndSetting={instrumentDataAndSetting}
          chartRef={chartRef}
        />
      </>
    );
  },
  (prev, next) => {
    return prev.series === next.series;
  }
);

export const HighChartMultipleLine = props => {
  const template = useTemplateSetting();

  switch (template) {
    case TOOL_TEMPLATE.victor:
      return <HighChartMultipleLineVictor {...props} />;
    case TOOL_TEMPLATE.quadrata:
      return <HighChartMultipleLineQuadrata {...props} />;
    default:
      throw new Error(`Template: ${template} do not exist.`);
  }
};
